import React, { useEffect, useMemo, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';

import { StashScopedStoreProvider } from '../../store/stash-scoped/context';
import { StashBoardDataLoaderDemo } from '../../store/stash-scoped/demo/StashBoardDataLoaderDemo';
import { StashBoardDataLoader } from '../../store/stash-scoped/data/StashBoardDataLoader';
import { SseStashUpdatesSource } from '../../store/stash-scoped/data/SseStashUpdatesSource';
import { DEMO_CURRENT_USER_ID } from '../../store/stash-scoped/demo/demoData';
import { ThemeToggle } from '../ThemeToggle';

import { AppShellProvider, useAppShell } from './AppShellContext';
import { AppSidebar } from './AppSidebar';
import { AppSidebarMobileBar } from './AppSidebarMobileBar';
import { AppTopBar } from './AppTopBar';

// Flip to `true` to bypass the streaming API and load the bundled demo fixtures
// (useful in Storybook / offline dev / before deploy). Production = SSE source.
const USE_DEMO_DATA = false;

function prefersReducedMotion(): boolean {
  return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}

interface AppShellProps {
  hideSidebar?: boolean;
}

function AppShellInner({ hideSidebar }: AppShellProps): React.JSX.Element {
  const location = useLocation();
  const { breadcrumb, topBarActions, topBarBack, setBreadcrumb, setTopBarActions, setTopBarBack } = useAppShell();
  const reducedMotion = prefersReducedMotion();
  const prevPathname = useRef(location.pathname);

  // Reset shell chrome only when the route actually changes — never on the
  // initial mount. (A mount-guard boolean is unsafe here: StrictMode invokes
  // effects twice on mount, and the second pass would wipe a breadcrumb a child
  // page just set.) Comparing the previous pathname is idempotent under that
  // double-invoke, so deep links / refreshes keep the page-provided breadcrumb.
  useEffect(() => {
    if (prevPathname.current === location.pathname) return;
    prevPathname.current = location.pathname;
    setBreadcrumb([]);
    setTopBarActions(null);
    setTopBarBack(null);
  }, [location.pathname, setBreadcrumb, setTopBarActions, setTopBarBack]);

  const transition = { type: 'tween' as const, duration: reducedMotion ? 0 : 0.25, ease: 'easeInOut' as const };

  return (
    <div className="flex min-h-screen bg-[var(--stash-surface-sunken)]">
      {!hideSidebar && (
        <div className="hidden md:flex">
          <AppSidebar />
        </div>
      )}

      <main className="flex flex-1 flex-col min-w-0">
        <AppTopBar
          breadcrumb={breadcrumb}
          onBack={topBarBack}
          rightActions={topBarActions ?? <ThemeToggle />}
        />

        <div className="flex-1 overflow-y-auto">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0, y: '-30%' }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: '30%' }}
              transition={transition}
              className="min-h-full"
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </div>

        <AppSidebarMobileBar />
      </main>
    </div>
  );
}

export function AppShell({ hideSidebar = false }: AppShellProps): React.JSX.Element {
  // Phase 7: memoize the SSE source so its identity is stable across re-renders;
  // the loader's useEffect depends on `source` and would otherwise re-open the
  // stream connection on every render.
  const source = useMemo(() => new SseStashUpdatesSource(), []);
  return (
    <StashScopedStoreProvider id="main-board" currentUserStasherId={DEMO_CURRENT_USER_ID}>
      {USE_DEMO_DATA ? <StashBoardDataLoaderDemo /> : <StashBoardDataLoader source={source} />}
      <AppShellProvider>
        <AppShellInner hideSidebar={hideSidebar} />
      </AppShellProvider>
    </StashScopedStoreProvider>
  );
}
